import React, { useState, useEffect } from "react";
import ConfigProvider from "./common/ConfigProvider";
import useDefaultTrebbleAntDTokenConfig from "./helper/useDefaultTrebbleAntDTokenConfig";
import PropTypes from "prop-types";
import TrebbleDropdown from "./common/Dropdown";
import TrebbleCheckbox from "./common/Checkbox";
import TrebbleDivider from "./common/Divider";
import TrebbleInfoTooltip from "./common/TrebbleInfoTooltip";
import ti18n from 'i18n!nls/SearchSettingsi18n';

const SearchSettingsContextMenu = ({ 
  useEditorTheme, 
  searchWidgetModel,
  open, 
  onOpenChange, 
  children,
  ...restProps 
}) => {
  const themeConfig = useDefaultTrebbleAntDTokenConfig({ useEditorTheme });
  
  // State for search settings
  const [settings, setSettings] = useState({
    autoJumpToNext: false,
    previewWithOperation: false,
    previewWithoutOperation: false
  });

  // Helper function for i18n
  const t = (key) => window.getI18n(ti18n, key);

  // Initialize settings from model when component mounts or opens
  useEffect(() => {
    if (open && searchWidgetModel) {
      setSettings({
        autoJumpToNext: searchWidgetModel.getAutoJumpToNext(),
        previewWithOperation: searchWidgetModel.getPreviewWithOperation(),
        previewWithoutOperation: searchWidgetModel.getPreviewWithoutOperation(),
      });
    }
  }, [open, searchWidgetModel]);

  // Handle auto jump setting change
  const handleAutoJumpChange = (e) => {
    const newValue = e.target.checked;
    setSettings(prev => ({ ...prev, autoJumpToNext: newValue }));
    if (searchWidgetModel) {
      searchWidgetModel.setAutoJumpToNext(newValue);
    }
  };

  // Handle preview with operation change (mutually exclusive)
  const handlePreviewWithOperationChange = (e) => {
    const newValue = e.target.checked;
    setSettings(prev => ({ 
      ...prev, 
      previewWithOperation: newValue,
      previewWithoutOperation: newValue ? false : prev.previewWithoutOperation
    }));
    if (searchWidgetModel) {
      searchWidgetModel.setPreviewWithOperation(newValue);
      if (newValue) {
        searchWidgetModel.setPreviewWithoutOperation(false);
      }
    }
  };

  // Handle preview without operation change (mutually exclusive)
  const handlePreviewWithoutOperationChange = (e) => {
    const newValue = e.target.checked;
    setSettings(prev => ({ 
      ...prev, 
      previewWithoutOperation: newValue,
      previewWithOperation: newValue ? false : prev.previewWithOperation
    }));
    if (searchWidgetModel) {
      searchWidgetModel.setPreviewWithoutOperation(newValue);
      if (newValue) {
        searchWidgetModel.setPreviewWithOperation(false);
      }
    }
  };

  const checkboxStyle = {
    marginLeft: themeConfig.token.marginXS,
    fontSize: themeConfig.token.fontSize,
    lineHeight: themeConfig.token.lineHeight,
    display: 'flex',
    alignItems: 'flex-start',
    gap: themeConfig.token.paddingXS,
    padding: `${themeConfig.token.paddingXS}px ${themeConfig.token.paddingSM}px`,
    borderRadius: themeConfig.token.borderRadiusXS,
    transition: 'all 0.2s'
  };

  const dropdownContent = (
    <div className="rounded shadow-lg flex flex-col p-4" style={{ 
      minWidth: '300px',
      color: themeConfig.token.colorText,
      fontFamily: themeConfig.token.fontFamily,
      borderRadius: themeConfig.token.borderRadiusSM
    }}>
      {/* Auto Jump Setting */}
      <div className="mb-4">
        <div className="flex items-center">
          <TrebbleCheckbox 
            useEditorTheme={useEditorTheme}
            checked={settings.autoJumpToNext}
            onChange={handleAutoJumpChange}
            className="checkbox-item"
            style={checkboxStyle}
          >
            <span className="font-medium">{t('AUTO_JUMP_TO_NEXT')}</span>
          </TrebbleCheckbox>
          <TrebbleInfoTooltip
            title={t('AUTO_JUMP_TO_NEXT_DESCRIPTION')}
            placement="top"
            useEditorTheme={useEditorTheme}
            marginLeft={8}
          />
        </div>
      </div>

      {/* Preview Settings */}
      <div className="flex flex-col space-y-3">
          {/* Preview with operation */}
          <div className="flex items-center">
            <TrebbleCheckbox 
              useEditorTheme={useEditorTheme}
              checked={settings.previewWithOperation}
              onChange={handlePreviewWithOperationChange}
              className="checkbox-item"
              style={checkboxStyle}
            >
              <span className="font-medium">{t('PREVIEW_WITH_OPERATION')}</span>
            </TrebbleCheckbox>
            <TrebbleInfoTooltip
              title={t('PREVIEW_WITH_OPERATION_DESCRIPTION')}
              placement="top"
              useEditorTheme={useEditorTheme}
              marginLeft={8}
            />
          </div>

          {/* Preview without operation */}
          <div className="flex items-center">
            <TrebbleCheckbox 
              useEditorTheme={useEditorTheme}
              checked={settings.previewWithoutOperation}
              onChange={handlePreviewWithoutOperationChange}
              className="checkbox-item"
              style={checkboxStyle}
            >
              <span className="font-medium">{t('PREVIEW_WITHOUT_OPERATION')}</span>
            </TrebbleCheckbox>
            <TrebbleInfoTooltip
              title={t('PREVIEW_WITHOUT_OPERATION_DESCRIPTION')}
              placement="top"
              useEditorTheme={useEditorTheme}
              marginLeft={8}
            />
          </div>
      </div>
    </div>
  );

  return (
    <ConfigProvider theme={themeConfig}>
      <style>
        {`
          .ant-dropdown {
            background: var(--color-white-10) !important;
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
          }
          .ant-dropdown-menu {
            background: transparent !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
          }
          .checkbox-item {
            width: 100%;
            margin-bottom: 0px !important;
          }
          .checkbox-item:hover {
            color: ${themeConfig.token.colorPrimaryActive};
            background-color: ${themeConfig.token.controlItemBgActiveHover} !important;
          }
          .ant-checkbox-wrapper + .ant-checkbox-wrapper {
            margin-left: 0;
          }
          .ant-divider {
            border-color: ${themeConfig.token.colorSplit};
          }
        `}
      </style>
      <TrebbleDropdown 
        useEditorTheme={useEditorTheme}
        dropdownRender={() => dropdownContent}
        trigger={['click']}
        open={open}
        onOpenChange={onOpenChange}
        placement="bottomRight"
        overlayClassName="trebble-search-settings-dropdown"
        {...restProps}
      >
        {children}
      </TrebbleDropdown>
    </ConfigProvider>
  );
};

SearchSettingsContextMenu.propTypes = {
  useEditorTheme: PropTypes.bool,
  searchWidgetModel: PropTypes.shape({
    getAutoJumpToNext: PropTypes.func.isRequired,
    setAutoJumpToNext: PropTypes.func.isRequired,
    getPreviewWithOperation: PropTypes.func.isRequired,
    setPreviewWithOperation: PropTypes.func.isRequired,
    getPreviewWithoutOperation: PropTypes.func.isRequired,
    setPreviewWithoutOperation: PropTypes.func.isRequired
  }),
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  children: PropTypes.node
};

export default SearchSettingsContextMenu;